import {gql} from '@apollo/client';
import {useState} from 'react';

import {assertNotNullAndReturn} from 'Asserts';
import {InaccessibleInfoNode, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getPowAgnosticProcedureIdFromProcedureRun,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {AllPMEIsInStudyQuery_study_StudyNode_procedureRuns_ProcedureRunNode as ProcedureRunNode} from 'studies/typed/shared/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';
import {
  AllPMEIsInStudyQuery,
  AllPMEIsInStudyQueryVariables,
} from 'studies/typed/shared/unit-op-linking-flowchart/__generated/AllPMEIsInStudyQuery';

import {UnitOperationStatusType} from '../useMultiProcedureRunStudyProgress';
import {UnitOpLinkEdge} from './UnitOpLinkingFlowchartUtils';

export function getOrderedPMDVIds(procedureRun: ProcedureRunNode): Array<string> {
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);

  if (isInaccessible(flowchartVersion)) {
    return []; // This is safe to do because we are only ordering the PMDV ids.
  }

  return flowchartVersion.nodes.map((node) => node.id);
}

const NO_ACCESS_STRING = '(No access)';

export const ALL_PMEIS_IN_STUDY_QUERY = gql`
  query AllPMEIsInStudyQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        procedureRuns {
          ... on ProcedureRunNode {
            id
            name
            displayId
            procedureType
            flowchartIsExpanded
            templateProcedureFlowchartVersion {
              ... on ProcedureFlowchartVersionNode {
                id
                nodes {
                  id
                }
                procedureFlowchart {
                  id
                  parent {
                    id
                  }
                }
              }
            }
            procedureFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
                nodes {
                  id
                }
                procedure {
                  id
                }
              }
              ... on InaccessibleInfoNode {
                ...InaccessibleInfoGraphQL
              }
            }
            conditionReplicateFlowchart {
              sourceProcedureMethodExecutionInstance {
                id
              }
              targetProcedureMethodExecutionInstance {
                id
              }
            }
            methodExecutionInstances {
              ... on ProcedureMethodExecutionInstanceNode {
                id
                templateMethodVersion {
                  ... on ProcedureMethodDefinitionVersionNode {
                    id
                    name
                  }
                  ...InaccessibleInfoGraphQL
                }
                targetProcedureMethodExecutionInstanceAssociations {
                  ... on ProcedureMethodExecutionInstanceAssociationNode {
                    id
                    sourceProcedureMethodExecutionInstance {
                      id
                    }
                    targetProcedureMethodExecutionInstance {
                      id
                    }
                  }
                }
                sourceProcedureMethodExecutionInstanceAssociations {
                  ... on ProcedureMethodExecutionInstanceAssociationNode {
                    id
                    sourceProcedureMethodExecutionInstance {
                      id
                    }
                    targetProcedureMethodExecutionInstance {
                      id
                    }
                  }
                }
                procedureRunConditionReplicate {
                  ... on ProcedureRunConditionReplicateNode {
                    id
                    replicateNumber
                    procedureRunCondition {
                      ... on ProcedureRunConditionNode {
                        id
                        name
                      }
                    }
                  }
                }
              }
              methodTask {
                ... on ProcessTaskNode {
                  id
                  status {
                    id
                    statusType
                  }
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface UnitOperationFields {
  pmeiId: string;
  procedureRunId: string;
  procedureRunName: string | null;
  procedureId?: string | null;
  /** Whether this unit operation is part of a procedure run plan instead of a procedure run. */
  isPlan?: boolean;
  procedureRunOrderKey: number;
  /**
   * The display ID of the procedure run. This is only used for the source procedure run,
   * and is not used for the destination procedure run.
   */
  procedureRunDisplayId: string | null;
  methodName: string;
  /**
   * The ID of the template method version. This is used for grouping PMEIs by method,
   * as it's unique even when methodName is "(No access)" for inaccessible methods.
   */
  templateMethodId: string;
  methodOrderKey: number;
  conditionName: string;
  conditionOrderKey: number;
  replicateNumber: number;
  procedureType: ProcedureType;
  status: UnitOperationStatusType | null;
}

interface ExistingDestinationInfo {
  procedureRunId: string;
  pmeis?: never;
}
interface NewDestinationInfo {
  pmeis: Array<UnitOperationFields>;
  procedureRunId?: never;
}

export enum UnitOpLinkingStateStatus {
  QUERY_ERROR = 'QUERY_ERROR',
  QUERY_PENDING = 'QUERY_PENDING',
  STUDY_INACCESSIBLE = 'STUDY_INACCESSIBLE',
  SUCCESS = 'SUCCESS',
}

export interface SuccessfulQueryUnitOpLinkingState {
  status: UnitOpLinkingStateStatus.SUCCESS;
  sourcePMEIs: Array<UnitOperationFields>;
  destinationPMEIs: Array<UnitOperationFields>;
  destinationIntraRunLinks: Array<UnitOpLinkEdge> | null;
  initialLinks: Array<UnitOpLinkEdge>; // Pre-existing links queried before user has made any edits
  newLinks: Array<UnitOpLinkEdge> | null; // Link state after the user makes changes, or null if no changes
  setNewLinks: (links: Array<UnitOpLinkEdge>) => void;
}

interface InaccessibleStudyUnitOpLinkingState {
  status: UnitOpLinkingStateStatus.STUDY_INACCESSIBLE;
  inaccessibleStudy: InaccessibleInfoNode;
}

interface QueryErrorUnitOpLinkingState {
  status: Exclude<
    UnitOpLinkingStateStatus,
    UnitOpLinkingStateStatus.SUCCESS | UnitOpLinkingStateStatus.STUDY_INACCESSIBLE
  >;
  inaccessibleStudy?: never;
}

export type NotSuccessfulQueryUnitOpLinkingState =
  | InaccessibleStudyUnitOpLinkingState
  | QueryErrorUnitOpLinkingState;

/**
 * Fetches data from the study for use by the UnitOpLinkingFlowchartEditor. This hook is used both during
 * experiment creation and after experiment creation. In the during experiment creation case, the destination
 * PMEIs are passed in via state from the parent component. In the after experiment creation case, the PMEIs
 * are fetched from the current procedureRunId.
 */
export default function useUnitOpLinkingState(
  studyId: string,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): SuccessfulQueryUnitOpLinkingState | NotSuccessfulQueryUnitOpLinkingState {
  const result = useBenchlingQuery<AllPMEIsInStudyQuery, AllPMEIsInStudyQueryVariables>(
    ALL_PMEIS_IN_STUDY_QUERY,
    {
      variables: {studyId},
      // renderCachedDataWhenRefetching must be false here to avoid use of incomplete data from other queries
      // after successful creation of a new run/experiment (at least until apollo-client is upgraded to 3.10+)
      // See issue "useQuery can unexpectedly return partial data"
      renderCachedDataWhenRefetching: false,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    }
  );

  // We need to run the useState hook for links regardless of whether or not the query was successful, to
  // meet the React constraint that the same number of hooks is run (in the same order) every render.
  // In the case that the query was not successful, we initialize to an empty array, and this value is unused.
  let initialLinks: Array<UnitOpLinkEdge> = [];
  let orderedSourcePMEIs: Array<UnitOperationFields> = [];
  let orderedDestinationPMEIs: Array<UnitOperationFields> = [];
  let destinationIntraRunLinks: Array<UnitOpLinkEdge> | null = null;
  if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const procedureRuns = accessibleObjs(result.data.study.procedureRuns);
    orderedSourcePMEIs = getOrderedSourcePMEIs(procedureRuns, destinationInfo);
    orderedDestinationPMEIs = getOrderedDestinationPMEIs(procedureRuns, destinationInfo);
    destinationIntraRunLinks = getIntraRunLinksForDestination(procedureRuns, destinationInfo);
    initialLinks = getExistingLinks(procedureRuns, orderedSourcePMEIs, orderedDestinationPMEIs);
  }
  const [newLinks, setNewLinks] = useState<Array<UnitOpLinkEdge> | null>(null);

  if (result.type === QueryResultType.ERROR) {
    return {
      status: UnitOpLinkingStateStatus.QUERY_ERROR,
    };
  } else if (result.type === QueryResultType.PENDING) {
    return {
      status: UnitOpLinkingStateStatus.QUERY_PENDING,
    };
  } else if (isInaccessible(result.data.study)) {
    return {
      status: UnitOpLinkingStateStatus.STUDY_INACCESSIBLE,
      inaccessibleStudy: result.data.study,
    };
  } else {
    // Query was successful and study is accessible.
    return {
      status: UnitOpLinkingStateStatus.SUCCESS,
      sourcePMEIs: orderedSourcePMEIs,
      destinationPMEIs: orderedDestinationPMEIs,
      destinationIntraRunLinks,
      initialLinks,
      newLinks,
      setNewLinks,
    };
  }
}

function getIntraRunLinksForDestination(
  procedureRuns: Array<ProcedureRunNode>,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): Array<UnitOpLinkEdge> | null {
  // When the procedure run has not yet been created, there is no condition replicate flowchart
  if (destinationInfo.procedureRunId == null) {
    return null;
  }
  const targetProcedureRun = assertNotNullAndReturn(
    procedureRuns.find((procedureRun) => procedureRun.id === destinationInfo.procedureRunId)
  );
  return targetProcedureRun.conditionReplicateFlowchart.map((edge) => ({
    sourcePMEIId: edge.sourceProcedureMethodExecutionInstance.id,
    destinationPMEIId: edge.targetProcedureMethodExecutionInstance.id,
  }));
}

export function getUnitOperationFields({
  pmei,
  pmeiIndex,
  procedureRun,
  runIndex,
  orderedPMDVIds,
  status,
}: {
  pmei: ProcedureRunNode['methodExecutionInstances'][number];
  pmeiIndex: number;
  procedureRun: ProcedureRunNode;
  runIndex: number;
  orderedPMDVIds: Array<string>;
  status: UnitOperationStatusType | null;
}): UnitOperationFields {
  return {
    pmeiId: pmei.id,
    procedureRunId: procedureRun.id,
    procedureRunName: procedureRun.name,
    procedureId: getPowAgnosticProcedureIdFromProcedureRun(procedureRun),
    isPlan: false,
    procedureRunOrderKey: runIndex,
    procedureRunDisplayId: procedureRun.displayId,
    methodName: !isInaccessible(pmei.templateMethodVersion)
      ? pmei.templateMethodVersion.name
      : NO_ACCESS_STRING,
    templateMethodId: getAccessibleOrInaccessibleId(pmei.templateMethodVersion),
    methodOrderKey: orderedPMDVIds.indexOf(getAccessibleOrInaccessibleId(pmei.templateMethodVersion)),
    conditionName: pmei.procedureRunConditionReplicate.procedureRunCondition.name,
    conditionOrderKey: pmeiIndex, // Assume PMEIs already correctly ordered w.r.t. conditions
    replicateNumber: pmei.procedureRunConditionReplicate.replicateNumber,
    procedureType: procedureRun.procedureType,
    status,
  };
}

function getOrderedSourcePMEIs(
  procedureRuns: Array<ProcedureRunNode>,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): Array<UnitOperationFields> {
  const sourceRuns = procedureRuns.filter(
    (procedureRun) => procedureRun.id !== destinationInfo.procedureRunId
  );
  return sourceRuns.flatMap((procedureRun, runIndex) => {
    const orderedPMDVIds = getOrderedPMDVIds(procedureRun);
    return procedureRun.methodExecutionInstances.map((pmei, pmeiIndex) =>
      getUnitOperationFields({
        pmei,
        pmeiIndex,
        procedureRun,
        runIndex,
        orderedPMDVIds,
        status: null,
      })
    );
  });
}

function getOrderedDestinationPMEIs(
  procedureRuns: Array<ProcedureRunNode>,
  destinationInfo: ExistingDestinationInfo | NewDestinationInfo
): Array<UnitOperationFields> {
  // New destination, just use the provided PMEIs
  if (destinationInfo.pmeis != null) {
    return destinationInfo.pmeis;
  }
  // Existing destination, grab PMEIs from the right run
  const targetProcedureRun = assertNotNullAndReturn(
    procedureRuns.find((procedureRun) => procedureRun.id === destinationInfo.procedureRunId)
  );
  const orderedPMDVIds = getOrderedPMDVIds(targetProcedureRun);
  return targetProcedureRun.methodExecutionInstances.map((pmei, pmeiIndex) =>
    getUnitOperationFields({
      pmei,
      pmeiIndex,
      procedureRun: targetProcedureRun,
      runIndex: 0, // Only one run allowed as destination
      orderedPMDVIds,
      status: null,
    })
  );
}

function getExistingLinks(
  queriedProcedureRuns: Array<ProcedureRunNode>,
  orderedSourcePMEIs: Array<UnitOperationFields>,
  orderedDestinationPMEIs: Array<UnitOperationFields>
): Array<UnitOpLinkEdge> {
  const links: Array<UnitOpLinkEdge> = [];
  const sourcePMEIIds = orderedSourcePMEIs.map((sourcePMEI) => sourcePMEI.pmeiId);
  const destinationPMEIIds = orderedDestinationPMEIs.map((destinationPMEI) => destinationPMEI.pmeiId);
  queriedProcedureRuns.forEach((procedureRun) => {
    procedureRun.methodExecutionInstances.forEach((pmei) => {
      pmei.targetProcedureMethodExecutionInstanceAssociations.forEach((association) => {
        if (!isInaccessible(association)) {
          const sourcePmeiId = association.sourceProcedureMethodExecutionInstance.id;
          const targetPmeiId = association.targetProcedureMethodExecutionInstance.id;
          if (sourcePmeiId && targetPmeiId) {
            if (sourcePMEIIds.includes(sourcePmeiId) && destinationPMEIIds.includes(targetPmeiId)) {
              links.push({sourcePMEIId: sourcePmeiId, destinationPMEIId: targetPmeiId});
            }
          }
        }
      });
    });
  });
  return links;
}
